import { TypedDocumentNode, gql } from '@apollo/client';
import { useRef } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { ANY_SPORT_PLAY_EVENTTYPE_FIXTURE } from '@sorare/core/src/constants/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';

import { RewardsBanner } from 'components/rewards/RewardBanner';

import { FlowEndButton_so5Fixture } from './__generated__/index.graphql';

type Props = {
  onClose: () => void;
  so5Fixture: FlowEndButton_so5Fixture;
  onClick?: (action?: () => void) => void;
};

export const FlowEndButton = ({ onClose, so5Fixture, onClick }: Props) => {
  const { generateSportContextPath } = useSportContext();
  const unclaimedRewards = useRef(
    so5Fixture.mySo5Rewards?.filter(reward => reward.aasmState !== 'claimed') ||
      []
  );
  const hasRewards = unclaimedRewards.current?.length > 0;

  return hasRewards ? (
    <RewardsBanner
      rewards={so5Fixture.mySo5Rewards.filter(
        reward => reward.aasmState !== 'claimed'
      )}
      onCloseClaimRewardsDialog={onClose}
      renderBanner={({ toggleShowClaimReward }) => (
        <Button
          size="medium"
          fullWidth
          color="secondary"
          onClick={() => {
            if (onClick) {
              onClick(toggleShowClaimReward);
              return;
            }
            toggleShowClaimReward();
          }}
        >
          <FormattedMessage
            id="FlowEndButton.claimRewards"
            defaultMessage="Claim rewards"
          />
        </Button>
      )}
    />
  ) : (
    <Button
      size="medium"
      fullWidth
      color="secondary"
      to={generateSportContextPath(ANY_SPORT_PLAY_EVENTTYPE_FIXTURE, {
        params: {
          eventType: so5Fixture.type,
          fixture: so5Fixture.slug,
        },
      })}
      onClick={onClose}
    >
      <FormattedMessage
        id="FlowEndButton.checkResults"
        defaultMessage="Check details"
      />
    </Button>
  );
};

FlowEndButton.fragments = {
  so5Fixture: gql`
    fragment FlowEndButton_so5Fixture on So5Fixture {
      slug
      id
      type
      mySo5Rewards {
        slug
        aasmState
        ...RewardsBanner_so5Reward
      }
    }
    ${RewardsBanner.fragments.so5Reward}
  ` as TypedDocumentNode<FlowEndButton_so5Fixture>,
};
